import {
  createFeedbackLoop,
  type FeedbackLoopOptions,
} from '@wix/feedback-loop';
import { EditorType } from '@wix/platform-editor-sdk';
import { userPreferences } from '@/stateManagement';

import { translate } from '#packages/i18n';
import type { EditorAPI } from '#packages/editorAPI';
import * as util from '#packages/util';
import constants from '#packages/constants';
import experiment from 'experiment';

const FEEDBACK_EXPERIMENT = 'se_aiThemeAssistant_feedback';
const FORCED_FEEDBACK_EXPERIMENT = 'se_aiThemeAssistant_feedbackForced';
const MIN_USER_MESSAGES = 1;

function getFeedbackModalCount(editorAPI: EditorAPI): number {
  return (
    userPreferences.selectors.getGlobalUserPreferences(
      constants.USER_PREFS.IS_FEEDBACK_MODAL_SEEN.AI_THEME_ASSISTANT,
    )(editorAPI.store.getState()) ?? 0
  );
}

function shouldShowFeedbackModal(
  editorAPI: EditorAPI,
  userMessageCount: number,
): boolean {
  if (experiment.isOpen(FORCED_FEEDBACK_EXPERIMENT)) return true;
  return (
    experiment.isOpen(FEEDBACK_EXPERIMENT) &&
    getFeedbackModalCount(editorAPI) < 1 &&
    userMessageCount >= MIN_USER_MESSAGES
  );
}

async function initFeedbackModal(editorAPI: EditorAPI) {
  const options: FeedbackLoopOptions = {
    debug: experiment.isOpen(FORCED_FEEDBACK_EXPERIMENT),
    esi: util.editorModel.editorSessionId,
    language: util.editorModel.languageCode,
    msid: editorAPI.dsRead.generalInfo.getMetaSiteId(),
    uuid: editorAPI.dsRead.generalInfo.getUserInfo().userId,
    editorType: EditorType.Classic,
    formSetId: 'theme-assistant-feedback',
    origin: 'theme-assistant-close',
    title: translate('theme_assistant_feedback_modal_title') ?? 'title',
    components: [
      {
        type: 'FacesRatingBar',
        questionId: 'rating',
        label:
          translate('theme_assistant_feedback_modal_rating_label') ?? 'rating',
      },
      {
        questionId: 'share-your-feedback',
        type: 'TextInput',
        label: translate('theme_assistant_feedback_modal_text_label') ?? 'text',
        placeholder: translate(
          'theme_assistant_feedback_modal_text_placeholder' ?? 'placeholder',
        ),
      },
    ],
  };
  const feedbackModal = createFeedbackLoop(options);
  const openResult = await feedbackModal.open();
  if (openResult.reason === 'not-shown') {
    throw new Error('Should show API returned an error');
  }
  const currentCount = getFeedbackModalCount(editorAPI);
  editorAPI.store.dispatch(
    userPreferences.actions.setGlobalUserPreferences(
      constants.USER_PREFS.IS_FEEDBACK_MODAL_SEEN.AI_THEME_ASSISTANT,
      currentCount + 1,
    ),
  );
}

export async function showFeedbackModalIfEnabled(
  editorAPI: EditorAPI,
  userMessageCount: number,
) {
  if (shouldShowFeedbackModal(editorAPI, userMessageCount)) {
    await initFeedbackModal(editorAPI);
  }
}
